import {
  analyzeParamsForTheLackOfClips,
  produceClipsChunked,
} from '@src/services/AIClipProducer';
import { err, ok } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import type { ClipFragment } from '@src/services/AIClipProducer';
import type { Result } from '@src/services/common/Result';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { RootStore } from '@src/stores-v2/RootStore';
import type { StoryVideo } from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * One in-flight search, cancellable. Same shape the producer's `abortToken`
 * takes, re-exported so callers don't reach into `AIClipProducer` for it.
 */
export interface ClipSearchToken {
  id: string;
  aborted: boolean;
}

/**
 * The producer's own handles, passed straight through.
 *
 * `produceClipsChunked` still takes the whole `IVideoCreatorStore` and
 * `RootStore` (it reads the story, the organization, the identity and four
 * repositories off them). Narrowing that is `TARGET-ARCHITECTURE.md` Phase 2's
 * AIClip decomposition, not this feature's job — so they are named here as
 * pass-throughs rather than quietly widened into this service's own surface.
 * Everything `ClipSearchService` itself uses is in `ClipSearchInput`.
 */
export interface ClipSearchDeps {
  videoCreator: IVideoCreatorStore;
  rootStore: RootStore;
}

export interface ClipSearchConfig {
  enableTextVerification: boolean;
  enableSpeakerFiltering: boolean;
  chunkSize: number;
}

export interface ClipSearchInput {
  /** What the user typed, verbatim. Becomes the producer's custom theme. */
  prompt: string;
  /** A target length in seconds, or null for "any" (the producer's 7–120). */
  targetDuration: number | null;
  originalVideo: StoryVideo;
  originalDuration: number;
  transcriptionElements: TranscriptElement[];
  transcriptionText: string;
  config: ClipSearchConfig;
  token: ClipSearchToken;
  /** Fires per chunk as candidates arrive, so the UI can stream them. */
  onPartialResults?: (candidates: ClipFragment[]) => void;
}

export interface ClipSearchOutput {
  candidates: ClipFragment[];
}

/**
 * Runs one open-ended clip search for the Find Clips chat (PLAT-11806).
 *
 * This is deliberately a **thin wrapper** over `produceClipsChunked`: the
 * custom-preset path, the speech-pace duration maths, the chunking, the
 * impact-score ranking and the near-duplicate dedup are all existing, tuned
 * behaviour, and re-deriving any of it here would fork it. The service's whole
 * job is to turn a typed sentence into that call's parameters, and turn its
 * output — or its silence — into a `Result`.
 *
 * Nothing is saved: a search returns candidates, and saving is a separate step
 * (`saveProducedClips`) the user triggers per card.
 *
 * Per CLAUDE.md guardrail 1 this lives here rather than as another method on
 * `AIClipProducer.ts`, which is a decomposition target.
 */
export class ClipSearchService {
  private readonly _deps: ClipSearchDeps;
  private readonly _logger = LogManager.getLogger('service:ClipSearchService');

  constructor(deps: ClipSearchDeps) {
    this._deps = deps;
  }

  async search(input: ClipSearchInput): Promise<Result<ClipSearchOutput>> {
    const prompt = input.prompt.trim();
    if (!prompt) {
      return err('VALIDATION', 'Describe the clip you want first.');
    }
    if (input.transcriptionElements.length === 0) {
      return err('PRECONDITION', 'This story has no transcript to search yet.');
    }

    const { videoCreator, rootStore } = this._deps;

    try {
      const candidates = await produceClipsChunked({
        videoCreator,
        rootStore,
        originalVideo: input.originalVideo,
        originalDuration: input.originalDuration,
        transcriptionElements: input.transcriptionElements,
        transcriptionText: input.transcriptionText,
        // The producer chunks what it submits; the chat always searches the
        // whole transcript, so both texts are the same.
        submitTranscriptionText: input.transcriptionText,
        duration: input.targetDuration,
        // 'Custom' + `customTheme` is the producer's existing open-ended
        // path — the same one the Clip Producer modal's custom preset uses.
        theme: 'Custom',
        customTheme: prompt,
        extraClipMetadata: { customPrompt: prompt },
        abortToken: input.token,
        onClipsProduced: input.onPartialResults,
        config: input.config,
      });

      if (input.token.aborted) {
        return err('PRECONDITION', 'Search cancelled.');
      }

      if (!candidates?.length) {
        // An empty result is ambiguous and we cannot disambiguate it here:
        // `AIFlow.run()` catches a step's failure and does not rethrow, so
        // `produceClipsChunked` returns `[]` both when the transcript genuinely
        // held nothing and when every AI request failed at the transport layer.
        //
        // So only ONE of the producer's diagnoses is safe to show: the
        // infeasible-duration one, which is derived from the numbers we passed
        // in rather than from the empty result, and is therefore true either
        // way. Anything else would blame the user's wording for what may have
        // been a dead backend — which is exactly what it did during PLAT-11806
        // bring-up, costing an afternoon chasing a prompt that was fine.
        const analysis =
          input.targetDuration == null
            ? null
            : analyzeParamsForTheLackOfClips({
                aborted: false,
                durations: {
                  selectedDuration: input.targetDuration,
                  originalDuration: input.originalDuration,
                },
              });

        this._logger.warn(
          'Clip search returned no candidates — nothing matched, or every AI request failed (the producer cannot tell these apart)',
          {
            targetDuration: input.targetDuration,
            originalDuration: input.originalDuration,
            elements: input.transcriptionElements.length,
          },
        );

        return err(
          'NOT_FOUND',
          analysis?.reason ??
            'Nothing came back for that. Try describing it another way — or if it keeps happening, the AI service may be unavailable.',
        );
      }

      return ok({ candidates });
    } catch (e) {
      // The producer throws on abort; a user's Stop is not a failure.
      if (input.token.aborted) {
        return err('PRECONDITION', 'Search cancelled.');
      }
      this._logger.error('Clip search failed', extractErrorInfo(e));
      return err(
        'EXTERNAL',
        'The search failed. Please try again.',
        extractErrorInfo(e),
      );
    }
  }
}
